import api from './api';

/**
 * Login with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {boolean} rememberMe - Whether to keep user logged in for extended period
 * @returns {Promise} - Returns auth response with token and user profile
 */
export const login = async (email, password, rememberMe = false) => {
  try {
    const response = await api.post('/Auth/login', { email, password, rememberMe });
    if (response.data.token) {
      // Store token in localStorage (short-lived access token)
      localStorage.setItem('token', response.data.token);
      // Store user profile in localStorage
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    // Refresh token is automatically stored as httpOnly cookie by the server
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

/**
 * Register a new user
 * @param {Object} userData - Registration data
 * @returns {Promise} - Returns auth response with token and user profile
 */
export const register = async (userData) => {
  try {
    const response = await api.post('/Auth/register', userData);
    if (response.data.token) {
      // Store token in localStorage
      localStorage.setItem('token', response.data.token);
      // Store user profile in localStorage
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

/**
 * Logout the current user
 * Calls backend to revoke refresh token and clears local storage
 */
export const logout = async () => {
  try {
    // Call backend to revoke refresh token (cookie will be cleared by server)
    await api.post('/Auth/logout');
  } catch (error) {
    // Even if backend call fails, still clear local storage
    console.error('Logout API call failed:', error);
  } finally {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
};

/**
 * Get current user from localStorage
 * @returns {Object|null} - User profile or null if not logged in
 */
export const getCurrentUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

/**
 * Check if user is authenticated
 * @returns {boolean} - True if user is authenticated
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

/**
 * Get user profile from API
 * @returns {Promise} - Returns user profile
 */
export const getProfile = async () => {
  try {
    const response = await api.get('/User/profile');
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

/**
 * Get user's license status
 * @returns {Promise} - Returns license status including trial info
 */
export const getLicenseStatus = async () => {
  try {
    const response = await api.get('/License/my-license-status');
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

/**
 * Request password reset email
 * @param {string} email - User email
 * @returns {Promise} - Returns success response
 */
export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/Auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    // Handle rate limit error (429 status code)
    if (error.response?.status === 429) {
      throw error.response.data;
    }
    throw error.response?.data || { message: error.message };
  }
};

/**
 * Validate password reset token
 * @param {string} token - Reset token
 * @returns {Promise} - Returns validation result
 */
export const validateResetToken = async (token) => {
  try {
    const response = await api.post('/Auth/validate-reset-token', { token });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

/**
 * Reset password with token
 * @param {string} token - Reset token
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Confirm password
 * @returns {Promise} - Returns success response
 */
export const resetPassword = async (token, newPassword, confirmPassword) => {
  try {
    const response = await api.post('/Auth/reset-password', {
      token,
      newPassword,
      confirmPassword
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

const authService = {
  login,
  register,
  logout,
  getCurrentUser,
  isAuthenticated,
  getProfile,
  getLicenseStatus,
  forgotPassword,
  validateResetToken,
  resetPassword
};

export default authService;